import { useState, useCallback } from 'react';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { SectionContainer, AnimatedHeading, GlassCard } from '../components/ui';
import { Link } from 'react-router-dom';
import { ScrollProgress, Reveal, WordReveal, ChapterLine } from '../components/motion/ScrollMotion';

const FAQS = [
  { q: 'What file formats does ARISE support?', a: 'The primary formats are Windows PE executables (.exe, .dll, .sys) and Linux ELF binaries. Other file types (including APK, documents and archives) are rejected and the analysis is not charged. Mach-O files are accepted, but Mach-O analysis has not been validated. The maximum upload size is set by the server operator (the hosted service uses 32 MB).' },
  { q: 'Is my uploaded binary stored permanently?', a: 'No. Uploaded files are stored in a temporary directory and automatically deleted after analysis completes (or if the analysis fails). Files are not retained beyond the analysis session.' },
  { q: 'How long does analysis take?', a: 'Analysis time depends on binary size, complexity, and which engines are available on the server; the hosted free tier may also need extra time to wake up after being idle. You can watch the 7-stage pipeline progress in real time on the dashboard.' },
  { q: 'What is CAPA and why is it important?', a: 'CAPA is an open-source tool developed by Mandiant/FireEye that identifies behavioral capabilities in executables using a library of over 1,000 YAML rules. It answers questions like "can this binary spawn new processes?" or "does it use process injection?" — without executing the file.' },
  { q: 'How does the MITRE ATT&CK mapping work?', a: 'CAPA rules are annotated with MITRE ATT&CK technique IDs. When CAPA fires a rule, ARISE extracts the associated technique IDs and shows them as candidate ATT&CK techniques for analyst review, not confirmed findings. If CAPA is disabled on the server, the report says so and these CAPA-derived candidates are absent.' },
  { q: 'What is the difference between Risk Score and CVSS?', a: "The Risk Score (0–100) is a rule-based heuristic computed from imports, strings and detected capabilities. The CVSS-style severity (0–10) is also a heuristic estimate: it is not an official CVSS v3.1 vector calculation and is not derived from the NVD." },
  { q: 'Does ARISE execute the malware?', a: 'No. ARISE performs entirely static analysis — the binary is never executed. All behavioral signals are derived from static code analysis, import tables, strings, and CAPA rule matching. This makes analysis safe to run on production infrastructure.' },
  { q: 'Can I use ARISE on a private/internal network?', a: 'The hosted ARISE service runs in the cloud, so uploaded files are sent to our server for analysis. The analysis pipeline itself makes no external API calls. Private or air-gapped deployments are not currently offered as a product.' },
  { q: 'What is Ghidra and when is it used?', a: 'Ghidra is an open-source reverse engineering framework developed by the NSA. When available, ARISE uses it as a secondary decompilation engine alongside Radare2, enabling deeper analysis including high-level pseudocode generation and improved function boundary detection.' },
  { q: 'How do I interpret a SUSPICIOUS verdict?', a: 'A SUSPICIOUS verdict means the binary exhibits some behaviors associated with malware (e.g., network communication, registry writes) but the confidence threshold for MALICIOUS classification was not met. It warrants further investigation — check the MITRE techniques and capabilities for specific concerns.' },
  { q: 'Can ARISE detect packed or obfuscated binaries?', a: 'ARISE will analyze packed binaries but may have reduced capability extraction since the actual code is encrypted or compressed. The analysis will indicate reduced extraction confidence. For packed samples, we recommend dynamic analysis as a complementary step.' },
  { q: 'What backend infrastructure is required?', a: 'The backend is a Python FastAPI service with an in-process job queue and a PostgreSQL database (SQLite for local development). Radare2 is required; CAPA and Ghidra are optional, and every report states which engines ran. See docs/DEPLOYMENT.md in the repository.' },
];

function FAQItem({ question, answer, isOpen, onToggle, index, id }) {
  const headingId = `faq-heading-${id}`;
  const bodyId    = `faq-body-${id}`;

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  }, [onToggle]);

  return (
    <Reveal delay={Math.min(index * 0.04, 0.3)} y={16} blur={3} style={{ marginBottom: '0.6rem' }}>
      <div
        className="glass"
        style={{
          borderColor: isOpen ? 'rgba(0,240,255,0.25)' : 'rgba(0,240,255,0.1)',
          background:   isOpen ? 'rgba(0,240,255,0.035)' : 'rgba(6,18,36,0.8)',
          transition: 'all 0.22s',
          overflow: 'hidden',
        }}
      >
        {/* Accordion trigger — semantically a button */}
        <button
          id={headingId}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          onClick={onToggle}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%', background: 'none', border: 'none',
            cursor: 'pointer', padding: '1.1rem 1.4rem',
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', textAlign: 'left', gap: '1rem',
          }}
        >
          <span style={{
            fontFamily: 'Rajdhani', fontWeight: 600, fontSize: 15,
            color: isOpen ? '#00f0ff' : '#e0f4ff',
            letterSpacing: 0.3, lineHeight: 1.35,
            transition: 'color 0.2s', flex: 1,
          }}>
            {question}
          </span>
          <motion.span
            animate={{ rotate: isOpen ? 45 : 0 }}
            transition={{ duration: 0.2 }}
            aria-hidden="true"
            style={{
              width: 20, height: 20, flexShrink: 0,
              border: `1px solid ${isOpen ? '#00f0ff' : 'rgba(0,240,255,0.3)'}`,
              borderRadius: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: isOpen ? '#00f0ff' : '#6b8fa8',
              fontSize: 14, fontFamily: 'monospace',
              transition: 'border-color 0.2s, color 0.2s',
            }}
          >
            +
          </motion.span>
        </button>

        {/* Answer panel */}
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              id={bodyId}
              role="region"
              aria-labelledby={headingId}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
              <p style={{
                fontFamily: 'Inter', fontSize: 14, color: '#6b8fa8',
                lineHeight: 1.8, margin: 0,
                padding: '0 1.4rem 1.1rem',
                borderTop: '1px solid rgba(0,240,255,0.08)',
                paddingTop: '0.9rem',
              }}>
                {answer}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
}

function FAQContent() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="page-wrapper" style={{ paddingTop: 64 }}>
      <ScrollProgress />
      <SectionContainer as="section">
        <AnimatedHeading
          center
          subtitle="Everything you need to know about ARISE, its pipeline, and how to interpret results."
          level={1}
        >
          <WordReveal text="Frequently Asked " style={{ color: '#e0f4ff' }} />
          <WordReveal text="Questions" className="neon-text" delay={0.4} />
        </AnimatedHeading>

        <div style={{ maxWidth: 820, margin: '0 auto' }}>
          {FAQS.map((faq, i) => (
            <FAQItem
              key={i}
              id={i}
              index={i}
              question={faq.q}
              answer={faq.a}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </div>

        {/* CTA */}
        <ChapterLine style={{ marginTop: '3.5rem' }} />
        <Reveal y={24} style={{ textAlign: 'center' }}>
          <GlassCard style={{ padding: 'clamp(1.75rem,4vw,2.5rem)', maxWidth: 540, margin: '0 auto' }}>
            <p style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 20, color: '#e0f4ff', marginBottom: '0.6rem' }}>
              Still have questions?
            </p>
            <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#6b8fa8', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              Try the live analysis — upload any binary and see how ARISE works in practice.
            </p>
            <Link to="/analyze" className="btn-primary">▸ Try ARISE Now</Link>
          </GlassCard>
        </Reveal>
      </SectionContainer>
    </div>
  );
}

/* reducedMotion="user": the accordion's height/rotate animations and the
 * rest of this page's framer motion honour prefers-reduced-motion. */
export default function FAQPage() {
  return (
    <MotionConfig reducedMotion="user">
      <FAQContent />
    </MotionConfig>
  );
}
